import {provideHttpClient, withInterceptorsFromDi} from '@angular/common/http';
import {ApplicationConfig, inject, provideZoneChangeDetection} from '@angular/core';
import {provideAnimationsAsync} from '@angular/platform-browser/animations/async';
import {provideRouter} from '@angular/router';
import {GROUNDWORKS_DEFAULT_TENANT_ID, GroundworksAuthConfig, provideGroundworksAuth} from '@groundworks/ngx-groundworks-auth';
import {IMAGEKIT_THUMBNAIL_CONFIG} from '@groundworks/ngx-groundworks-ui';
import {routes} from './app.routes';
import {ConfigService} from './config.service';
import {resolveApiUrl} from './resolve-api-url';
import {ImageKitThumbnailConfigFactory} from './factories/imagekit-thumbnail-config-factory';
import {API_BASE_URL} from "./sdk/sdk";
import {BranchService} from './services/branch.service';
import {ProfileService} from './services/profile.service';

export function buildAuthConfig(): GroundworksAuthConfig {
  const config = inject(ConfigService);
  const graphUri = config.graph.uri.trim();
  const graphScopes = config.graph.scopes.map(scope => scope.trim()).filter(scope => scope !== '');
  return {
    clientId: config.clientId,
    tenantId: GROUNDWORKS_DEFAULT_TENANT_ID,
    redirectUri: config.authRedirectUrl,
    apiUrl: resolveApiUrl(config),
    // env.js values are untyped at runtime (entry_point.sh writes `production` unquoted); Boolean()
    // keeps the pre-migration truthiness semantics instead of failing the library's boolean check.
    production: Boolean(config.production),
    // The library rejects a blank graph config.
    ...(graphUri !== '' && graphScopes.length > 0 ? {graph: {uri: graphUri, scopes: graphScopes}} : {}),
    hooks: {
      afterInteractionComplete: async (account) => {
        // inject() only works before the first await.
        const profile = inject(ProfileService);
        const branches = inject(BranchService);
        if (!account.idTokenClaims) return;
        await profile.initialize();
        await branches.setDefaultBranchByName(profile.profile$.value?.officeLocation);
      },
    },
  };
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({eventCoalescing: true}),
    provideAnimationsAsync(),
    provideHttpClient(
      withInterceptorsFromDi()
    ),
    {
      provide: API_BASE_URL,
      useFactory: (config: ConfigService) => resolveApiUrl(config),
      deps: [ConfigService]
    },
    {
      provide: IMAGEKIT_THUMBNAIL_CONFIG,
      useFactory: ImageKitThumbnailConfigFactory,
      deps: [ConfigService]
    },
    provideRouter(routes),
    provideGroundworksAuth(buildAuthConfig),
  ]
};
